// Measurement catch-up reminder widget (BODY_MEASUREMENTS_PLAN.md Task 6.2).
// Reminder-only, like VitalsReminderWidget: no per-day backfill makes sense
// for a measuring session, so this just nudges back to the Measurements tab
// once the patient has gone longer than their configured reminderDays. Off
// by default (D-MEAS-14); never nags someone who has never used the feature
// (MEASUREMENT_ACTIVE_MIN_SESSIONS gate lives in the API sweep).
import { useEffect, useState } from "react";
import type { CatchUpResponse } from "@medassist/shared";
import { resolveCatchUpWidgets, resolveProactiveSettings } from "@medassist/shared";
import { getCatchUpData } from "../../../lib/catchUpFetch.js";
import { ApiError } from "../../../lib/api.js";
import { RulerIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

export function MeasurementCatchUpWidget({ patient, onError }: WidgetProps) {
  const [response, setResponse] = useState<CatchUpResponse | null>(null);

  const enabled = resolveCatchUpWidgets(
    resolveProactiveSettings(patient.proactiveSuggestions).catchUpWidgets
  ).measurement;

  useEffect(() => {
    if (!enabled) return;
    getCatchUpData(patient.patientId)
      .then(setResponse)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load measurement reminder"));
  }, [patient.patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const section = response?.sections.find((s) => s.kind === "measurement");
  if (!section) return null;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <RulerIcon size={16} className="text-amber-500 dark:text-amber-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Measurement reminder</h2>
        <button
          onClick={() => { location.hash = "#/body?tab=measurements"; }}
          className="ml-auto text-xs text-(--accent) hover:underline"
        >
          Log measurements
        </button>
      </div>
      <p className="text-sm text-amber-700 dark:text-amber-300">
        {section.daysSince} days since your last measuring session (reminder set to {section.reminderDays})
      </p>
    </section>
  );
}
